import { useEffect, useState } from "react";
import { duplicateVoyage } from "@/lib/voyagesApi";
import { sameWeekdayNextMonth } from "@/lib/dateHelpers";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function DuplicateDialog({ open, onOpenChange, voyage, onSuccess }) {
  const navigate = useNavigate();
  const [voyageNumber, setVoyageNumber] = useState("");
  const [startDate, setStartDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Pre-fill smart defaults whenever the dialog opens or the target voyage
  // changes. Doing this inside Dialog's `onOpenChange` callback isn't
  // reliable — Radix doesn't always invoke that with (true) when the parent
  // flips `open` directly, which left the inputs blank.
  useEffect(() => {
    if (!open || !voyage) return;
    setVoyageNumber(`${voyage.voyage_number}-CLONE`);
    setStartDate(sameWeekdayNextMonth(voyage.start_date));
    setError(null);
  }, [open, voyage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!voyage) return;
    setSaving(true);
    setError(null);
    try {
      const result = await duplicateVoyage(voyage.id, {
        voyage_number: voyageNumber,
        start_date: startDate,
      });
      onSuccess();
      navigate(`/voyages/${result.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Duplicate failed";
      setError(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="duplicate-dialog">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Duplicate Voyage</DialogTitle>
            <DialogDescription>
              Create a copy of <span className="font-semibold">{voyage?.voyage_number}</span> with a new voyage number and start date.
            </DialogDescription>
          </DialogHeader>
          <div className="my-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="dup-voyage-number">New Voyage Number</Label>
              <Input
                id="dup-voyage-number" value={voyageNumber}
                onChange={(e) => setVoyageNumber(e.target.value)}
                required data-testid="dup-voyage-number"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dup-start-date">New Start Date</Label>
              <Input
                id="dup-start-date" type="date" value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required data-testid="dup-start-date"
              />
            </div>
          </div>
          {error && (
            <div className="mb-4 rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive" data-testid="dup-error">
              {error}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="dup-submit-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Duplicate
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
